import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { TagDictionaryItem, TagDictionaryService } from '@app/services/tag-dictionary.service';
import { TagModalComponent } from '@app/components/tag-modal/tag-modal.component';
import { TooltipModule } from 'primeng/tooltip';

export interface TagOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-tag-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TagModalComponent, TooltipModule],
  templateUrl: './tag-selector.component.html',
  styleUrls: ['./tag-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagSelectorComponent implements OnInit, OnDestroy {
  @ViewChild('triggerButton') triggerButtonRef!: ElementRef<HTMLElement>;
  @ViewChild('dropdown') dropdownRef!: ElementRef<HTMLElement>;

  @Input() selectedTagIds: string[] = [];
  @Input() label: string = '';
  @Input() placeholder: string = 'UPLOAD_IMAGES.MODAL_TAGS_PLACEHOLDER';
  @Input() disabled: boolean = false;
  @Input() allowCreate: boolean = true;
  @Input() readonly: boolean = false; // Режим просмотра (только чтение)
  @Input() displayTagNames: string[] = []; // Массив имен тегов для режима просмотра

  @Output() selectedTagIdsChange = new EventEmitter<string[]>();
  @Output() tagsChange = new EventEmitter<string[]>();

  isOpen = false;
  existingTags: TagDictionaryItem[] = [];
  tagSearchTerm = '';

  // Позиционирование dropdown (для appendTo body)
  dropdownTop = 0;
  dropdownLeft = 0;
  dropdownWidth = 0;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private originalParent: HTMLElement | null = null;
  private originalNextSibling: Node | null = null;
  isCreateModalOpen = false;
  createModalInitialName = '';

  constructor(
    private tagService: TagDictionaryService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadExistingTags();

    // Debounce поиска тегов
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    // Возвращаем dropdown обратно перед уничтожением
    this.restoreDropdownFromBody();
    // Удаляем обработчик клика на модалке
    this.detachModalClickHandler();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadExistingTags(): void {
    this.tagService
      .getVisibleTags()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tags) => {
          this.existingTags = tags;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading existing tags:', error);
          this.existingTags = [];
          this.cdr.markForCheck();
        },
      });
  }

  get tagOptions(): TagOption[] {
    return this.existingTags.map((tag) => ({
      label: tag.name,
      value: tag.id,
    }));
  }

  get filteredTagOptions(): TagOption[] {
    const search = this.tagSearchTerm.trim().toLowerCase();
    if (!search) {
      return this.tagOptions;
    }
    return this.tagOptions.filter((option) => option.label.toLowerCase().includes(search));
  }

  get selectedTagsDisplay(): string {
    // В режиме просмотра используем displayTagNames
    if (this.readonly) {
      if (this.displayTagNames.length === 0) {
        return this.translate.instant('UPLOAD_IMAGES.MODAL_TAGS_PLACEHOLDER') || 'Нет тегов';
      }
      if (this.displayTagNames.length === 1) {
        return this.displayTagNames[0];
      }
      return `${this.displayTagNames.length} тегов`;
    }

    // В режиме редактирования
    if (this.selectedTagIds.length === 0) {
      return this.translate.instant('UPLOAD_IMAGES.MODAL_TAGS_PLACEHOLDER');
    }
    if (this.selectedTagIds.length === 1) {
      const tag = this.existingTags.find((t) => t.id === this.selectedTagIds[0]);
      return tag?.name || this.selectedTagIds[0];
    }
    return (
      this.translate.instant('UPLOAD_IMAGES.MODAL_TAGS_MULTIPLE_SELECTED', { count: this.selectedTagIds.length }) ||
      `${this.selectedTagIds.length} тегов выбрано`
    );
  }

  toggleDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      if (this.existingTags.length === 0) {
        this.loadExistingTags();
      }
      // Перемещаем dropdown в body и обновляем позицию
      requestAnimationFrame(() => {
        this.appendDropdownToBody();
        this.updateDropdownPosition();
      });

      // Добавляем обработчик клика на модалку, если событие не всплывает до document
      setTimeout(() => {
        this.attachModalClickHandler();
      }, 100);
    } else {
      // Возвращаем dropdown обратно
      this.restoreDropdownFromBody();
      this.detachModalClickHandler();
    }

    this.cdr.detectChanges();
  }

  private modalClickHandler: ((event: Event) => void) | null = null;

  private attachModalClickHandler(): void {
    if (this.modalClickHandler) {
      return;
    }

    this.modalClickHandler = (event: Event) => {
      if (!this.isOpen) {
        return;
      }

      const target = event.target as HTMLElement;

      // Проверяем клик внутри tag-selector
      const clickedInsideTagSelector = target.closest('app-tag-selector');
      const clickedInsideDropdown = this.dropdownRef?.nativeElement?.contains(target);
      const clickedOnTrigger = this.triggerButtonRef?.nativeElement?.contains(target);

      // Если клик не внутри tag-selector, закрываем dropdown
      if (!clickedInsideTagSelector && !clickedInsideDropdown && !clickedOnTrigger) {
        this.isOpen = false;
        this.restoreDropdownFromBody();
        this.cdr.detectChanges();
      }
    };

    // Находим dialog элемент и добавляем обработчик
    // Поддерживаем разные типы модалок: .dialog, app-dialog, .modal-overlay, .modal-content
    const dialogElement =
      document.querySelector('.dialog') ||
      document.querySelector('app-dialog') ||
      document.querySelector('.modal-overlay') ||
      document.querySelector('.modal-content');
    if (dialogElement) {
      dialogElement.addEventListener('click', this.modalClickHandler, true); // useCapture = true
    }
  }

  private detachModalClickHandler(): void {
    if (!this.modalClickHandler) {
      return;
    }

    // Поддерживаем разные типы модалок
    const dialogElement =
      document.querySelector('.dialog') ||
      document.querySelector('app-dialog') ||
      document.querySelector('.modal-overlay') ||
      document.querySelector('.modal-content');
    if (dialogElement && this.modalClickHandler) {
      dialogElement.removeEventListener('click', this.modalClickHandler, true);
    }

    this.modalClickHandler = null;
  }

  private appendDropdownToBody(): void {
    if (!this.dropdownRef?.nativeElement) {
      return;
    }

    const dropdownElement = this.dropdownRef.nativeElement;

    // Сохраняем оригинальную позицию
    if (!this.originalParent && dropdownElement.parentElement) {
      this.originalParent = dropdownElement.parentElement;
      this.originalNextSibling = dropdownElement.nextSibling;
    }

    // Перемещаем в body, если еще не там
    if (dropdownElement.parentElement !== document.body) {
      document.body.appendChild(dropdownElement);
    }
  }

  private restoreDropdownFromBody(): void {
    if (!this.dropdownRef?.nativeElement || !this.originalParent) {
      return;
    }

    const dropdownElement = this.dropdownRef.nativeElement;

    // Возвращаем на место, если еще в body
    if (dropdownElement.parentElement === document.body) {
      if (this.originalNextSibling) {
        this.originalParent.insertBefore(dropdownElement, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(dropdownElement);
      }
    }

    // Сбрасываем сохраненную позицию
    this.originalParent = null;
    this.originalNextSibling = null;
  }

  private updateDropdownPosition(): void {
    if (!this.triggerButtonRef?.nativeElement || !this.isOpen || !this.dropdownRef?.nativeElement) {
      return;
    }

    const buttonRect = this.triggerButtonRef.nativeElement.getBoundingClientRect();
    const dropdownElement = this.dropdownRef.nativeElement;
    const dropdownHeight = 360; // max-height из CSS
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spacing = 8;

    // Позиция сверху - привязываем к нижней границе кнопки
    let top = buttonRect.bottom + spacing;

    // Если не помещается снизу, показываем сверху
    if (top + dropdownHeight > viewportHeight && buttonRect.top > dropdownHeight) {
      top = buttonRect.top - dropdownHeight - spacing;
    }

    // Позиция слева - привязываем к левому краю кнопки
    let left = buttonRect.left;
    const width = buttonRect.width;

    // Корректируем позицию, если выходит за границы viewport
    if (left + width > viewportWidth) {
      left = Math.max(spacing, viewportWidth - width - spacing);
    }
    if (left < spacing) {
      left = spacing;
    }

    // Убеждаемся, что не выходит за границы
    const finalTop = Math.max(spacing, Math.min(top, viewportHeight - dropdownHeight - spacing));
    const finalLeft = Math.max(spacing, Math.min(left, viewportWidth - width - spacing));
    const finalWidth = Math.min(width, viewportWidth - spacing * 2);

    this.dropdownTop = finalTop;
    this.dropdownLeft = finalLeft;
    this.dropdownWidth = finalWidth;

    // Применяем позицию
    dropdownElement.style.top = `${finalTop}px`;
    dropdownElement.style.left = `${finalLeft}px`;
    dropdownElement.style.width = `${finalWidth}px`;

    this.cdr.detectChanges();
  }

  isTagSelected(tagId: string): boolean {
    return this.selectedTagIds.includes(tagId);
  }

  toggleTagSelection(tagId: string, event?: Event): void {
    event?.stopPropagation();

    if (this.disabled) {
      return;
    }

    let newSelectedTagIds: string[];

    if (this.isTagSelected(tagId)) {
      newSelectedTagIds = this.selectedTagIds.filter((id) => id !== tagId);
    } else {
      newSelectedTagIds = [...this.selectedTagIds, tagId];
    }

    this.emitSelectionChange(newSelectedTagIds);
  }

  getTagName(id: string): string {
    return this.existingTags.find((tag) => tag.id === id)?.name || id;
  }

  removeTag(id: string, event: Event): void {
    event.stopPropagation();

    if (this.disabled || this.readonly) {
      return;
    }

    this.emitSelectionChange(this.selectedTagIds.filter((tagId) => tagId !== id));
  }

  private emitSelectionChange(newSelectedTagIds: string[]): void {
    this.selectedTagIds = newSelectedTagIds;
    this.selectedTagIdsChange.emit(newSelectedTagIds);
    this.tagsChange.emit(newSelectedTagIds);
    this.cdr.detectChanges();
  }

  onTagSearchChange(): void {
    this.searchSubject.next(this.tagSearchTerm);
  }

  openCreateTagModal(event?: Event): void {
    event?.stopPropagation();
    if (!this.allowCreate || this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.isOpen = false;
      this.restoreDropdownFromBody();
    }
    this.createModalInitialName = this.tagSearchTerm.trim();
    this.isCreateModalOpen = true;
    this.cdr.detectChanges();
  }

  canShowCreateButton(): boolean {
    const name = this.tagSearchTerm.trim();
    if (!name) {
      return false;
    }
    return !this.existingTags.some((t) => t.name.toLowerCase() === name.toLowerCase());
  }

  handleTagCreated(tag: TagDictionaryItem): void {
    const exists = this.existingTags.some((t) => t.id === tag.id);
    const nextTags = exists ? this.existingTags.map((t) => (t.id === tag.id ? tag : t)) : [...this.existingTags, tag];
    this.existingTags = nextTags;

    if (!this.selectedTagIds.includes(tag.id)) {
      this.emitSelectionChange([...this.selectedTagIds, tag.id]);
    }

    this.tagSearchTerm = '';
    this.isCreateModalOpen = false;
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }

    // Игнорируем события, которые произошли во время drag операции
    if ((event as any).dataTransfer) {
      return;
    }

    const target = event.target as HTMLElement;

    // Проверяем клик внутри компонента tag-selector
    const clickedInsideTagSelector = target.closest('app-tag-selector');

    // Проверяем клик внутри dropdown (который может быть в body)
    const clickedInsideDropdown = this.dropdownRef?.nativeElement?.contains(target);
    const clickedOnTrigger = this.triggerButtonRef?.nativeElement?.contains(target);

    // Проверяем клик по dialog-backdrop (backdrop модалки)
    const clickedOnDialogBackdrop = target.closest('.dialog-backdrop');

    // Проверяем клик внутри dialog/modal, но вне tag-selector
    // Поддерживаем разные типы модалок
    const clickedInsideDialog =
      target.closest('.dialog') ||
      target.closest('app-dialog') ||
      target.closest('.modal-overlay') ||
      target.closest('.modal-content');

    // Если клик был внутри селектора или dropdown, не закрываем
    if (clickedInsideTagSelector || clickedInsideDropdown || clickedOnTrigger) {
      return;
    }

    // Если клик по backdrop - закрываем
    if (clickedOnDialogBackdrop) {
      this.isOpen = false;
      this.restoreDropdownFromBody();
      this.cdr.detectChanges();
      return;
    }

    // Если клик внутри dialog, но вне tag-selector - закрываем
    // (так как в dialog есть stopPropagation, клик может не всплыть до document,
    // поэтому проверяем через setTimeout, чтобы событие успело обработаться)
    if (clickedInsideDialog && !clickedInsideTagSelector) {
      // Используем setTimeout, чтобы событие успело всплыть через все обработчики
      setTimeout(() => {
        if (this.isOpen) {
          // Проверяем еще раз, не был ли это клик по tag-selector
          const currentTarget = document.elementFromPoint(event.clientX, event.clientY);
          if (
            currentTarget &&
            !currentTarget.closest('app-tag-selector') &&
            !this.dropdownRef?.nativeElement?.contains(currentTarget)
          ) {
            this.isOpen = false;
            this.restoreDropdownFromBody();
            this.cdr.detectChanges();
          }
        }
      }, 0);
      return;
    }

    // Закрываем выпадашку при клике вне области (обычный случай)
    this.isOpen = false;
    this.restoreDropdownFromBody();
    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      requestAnimationFrame(() => {
        this.updateDropdownPosition();
      });
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isOpen) {
      requestAnimationFrame(() => {
        this.updateDropdownPosition();
      });
    }
  }
}
